import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { activityOptions, stateOptions } from "../../../utils/options";

/* ---------------- FIELD OPTIONS ---------------- */
const FIELD_OPTIONS = {
  "Full Name": ["Is", "Contains"],
  "First Name": ["Is", "Contains"],
  "Last Name": ["Is", "Contains"],
  "Company Name": ["Is", "Starts With", "Is Empty", "Is Not Empty"],
  "Primary Phone": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Primary Email": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Street Address": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  City: ["Is", "Contains", "Is Empty", "Is Not Empty"],
  State: ["Is", "Is Not", "Is Any", "Is All", "Exclude Any", "Is Empty", "Is Not Empty"],
  "Zip Code": ["Is", "Contains", "Starts With", "Is Empty", "Is Not Empty"],
  Tags: ["Is", "Is Not", "Is Any", "Is All", "Exclude Any", "Is Empty", "Is Not Empty"],
  Website: ["Is Empty", "Is Not Empty"],
  "Has Acquisition Criteria": ["Is Empty", "Is Not Empty"],
  "First Deal Anniversary": ["Is Empty", "Is Not Empty"],
  "Tax Record Sent": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],
  "Lead Source": ["Is", "Is Not", "Is Any", "Exclude Any", "Is Empty", "Is Not Empty"],
  "Users": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Interaction Type": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Last Contacted": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],
  "Property Name": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Own X Amount of Properties": [
    "Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"
  ],
  "Activity Range": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],

  // ✅ Exclude Contact with sub-filter support
  "Exclude Contact": ["Matching", "Is Empty", "Is Not Empty"],
};

/* --------- FIELDS THAT USE DROPDOWN --------- */
const DROPDOWN_FIELDS = {
  "Company Name": "companies",
  Tags: "contact_tags",
  "Property Name": "properties_unique",
  "Users": "users",
  "Lead Source": "lead_sources",
  State: "state",
  "Interaction Type": "activity",
};

// ✅ Sub-filter fields available for "Exclude Contact" criteria
const EXCLUDE_CONTACT_SUB_FIELDS = {
  "Full Name": ["Is", "Contains"],
  "First Name": ["Is", "Contains"],
  "Last Name": ["Is", "Contains"],
  "Company Name": ["Is", "Starts With", "Is Empty", "Is Not Empty"],
  "Primary Phone": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Primary Email": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Street Address": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  City: ["Is", "Contains", "Is Empty", "Is Not Empty"],
  State: ["Is", "Is Not", "Is Any", "Is All", "Exclude Any", "Is Empty", "Is Not Empty"],
  "Zip Code": ["Is", "Contains", "Starts With", "Is Empty", "Is Not Empty"],
  Tags: ["Is", "Is Not", "Is Any", "Is All", "Exclude Any", "Is Empty", "Is Not Empty"],
  Website: ["Is Empty", "Is Not Empty"],
  "Has Acquisition Criteria": ["Is Empty", "Is Not Empty"],
  "First Deal Anniversary": ["Is Empty", "Is Not Empty"],
  "Tax Record Sent": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],
  "Lead Source": ["Is", "Is Not", "Is Any", "Exclude Any", "Is Empty", "Is Not Empty"],
  "Users": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Interaction Type": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Last Contacted": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],
  "Property Name": ["Is", "Is Not", "Is Any", "Is All", "Exclude Any"],
  "Own X Amount of Properties": [
    "Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"
  ],
  "Activity Range": [
    "On", "Before", "After", "Between", "In Last X Days", "Longer Than X Days",
    "Is Empty", "Is Not Empty"
  ],
};

/* ---------------- JOIN OPTIONS ---------------- */
const JOIN_OPTIONS = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const DATE_FIELDS = ["Tax Record Sent", "Last Contacted", "Activity Range"];
const NUMERIC_FIELDS = ["Own X Amount of Properties"];

const PREFIX_MAP = {
  "Tax Record Sent": "tax_record_",
  "Last Contacted": "last_contacted_",
  "Activity Range": "activity_",
  "Own X Amount of Properties": "property_",
};

const EXCLUDE_CONTACT_FIELD = "Exclude Contact";
const EXCLUDE_SUB_DATE_FIELDS = ["Last Contacted"];
const EXCLUDE_SUB_PREFIX_MAP = {
  "Last Contacted": "exclude_last_contacted_",
};

function ContactAdvanceFilterData({ filterData, onSetFilterData, isSidebarOpen }) {
  const [config] = useAuth();

  const [groups, setGroups] = useState(
    filterData && filterData.length > 0
      ? filterData
      : [
          {
            id: Date.now(),
            condition: "AND",
            filters: [{ join: null, field: "", operator: "", value: "" }],
          },
        ]
  );

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [showGroupSelector, setShowGroupSelector] = useState(false);

  useEffect(() => {
    if (isSidebarOpen && filterData && filterData.length > 0) {
      setGroups(filterData);

      // Pre-fetch dropdown options (including Exclude Contact)
      filterData.forEach((group) => {
        group.filters.forEach((filter) => {
          if (filter.field && DROPDOWN_FIELDS[filter.field] && !dropdownOptions[filter.field]) {
            fetchDropdownOptions(filter.field).then((opts) =>
              setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }))
            );
          }
        });
      });
    }
  }, [isSidebarOpen, filterData]);

  /* ---------------- UPDATE STATE ---------------- */
  const updateState = (updated) => {
    setGroups(updated);
    onSetFilterData(updated);
  };

  /* ---------------- API FETCHER ---------------- */
  const fetchDropdownOptions = async (field, search = "") => {
    const list = DROPDOWN_FIELDS[field];
    if (!list) return [];

    if (list === "state" || list === "activity") {
      return list === "state" ? stateOptions : activityOptions;
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/get-datalisting?list=${list}&search=${search}`,
        config
      );

      const data = res?.data?.data || {};

      if (list === "companies") {
        return data.company_list?.map((el) => ({
          label: el.company_name,
          value: el.id,
        })) || [];
      }
      if (list === "contact_tags") {
        return data.contact_tags?.map((el) => ({
          label: el.tag_name,
          value: el.id,
        })) || [];
      }
      if (list === "users") {
        return data.map((el) => ({
          label: `${el.first_name} ${el.last_name}`,
          value: el.id,
        })) || [];
      }
      if (list === "properties_unique") {
        return data.properties?.map((el) => ({
          label: el.property_name,
          value: el.id,
        })) || [];
      }
      if (list === "lead_sources") {
        return data.lead_sources?.map((el) => ({
          label: el.name,
          value: el.id,
          category: el.category,
        })) || [];
      }
      if (list === "contacts") {
        return data.contact_list?.map((el) => ({
          label: `${el.first_name} ${el.last_name}`,
          value: el.id,
        })) || [];
      }

      return [];
    } catch (err) {
      if (err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message || "Failed to load options");
      }
      return [];
    }
  };

  /* ---------------- ROW HANDLERS ---------------- */
  const addFilterRow = (groupIndex) => {
    const updated = [...groups];
    updated[groupIndex].filters.push({ join: "AND", field: "", operator: "", value: "" });
    updateState(updated);
  };

  const updateRow = (groupIndex, filterIndex, key, value) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];

    if (key === "field") {
      const newField = value?.value ?? value;
      filter.field = newField;
      filter.operator = "";

      const isDateField = DATE_FIELDS.includes(newField);
      const isNumericField = NUMERIC_FIELDS.includes(newField);
      const isExcludeContactField = newField === EXCLUDE_CONTACT_FIELD;

      if (isDateField || isNumericField) {
        const prefix = PREFIX_MAP[newField] || "";
        filter.value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else if (isExcludeContactField) {
        filter.value = { subFilters: [{ field: "", operator: "", value: "" }] };
      } else {
        filter.value = "";
      }

      // Fetch dropdown options if needed
      if (DROPDOWN_FIELDS[newField] && !dropdownOptions[newField]) {
        fetchDropdownOptions(newField).then((opts) =>
          setDropdownOptions((prev) => ({ ...prev, [newField]: opts }))
        );
      }
    } 
    else if (key === "operator") {
      const newOp = value?.value ?? value;
      filter.operator = newOp;

      const isMultiOp = ["Is Any", "Is All", "Exclude Any"].includes(newOp);
      const isExcludeContactField = filter.field === EXCLUDE_CONTACT_FIELD;

      if (isExcludeContactField) {
        // Exclude Contact uses nested sub-filters
        if (newOp === "Matching") {
          filter.value = { subFilters: [{ field: "", operator: "", value: "" }] };
        } else {
          filter.value = "";
        }
      } else if (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) {
        const prefix = PREFIX_MAP[filter.field] || "";
        filter.value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        filter.value = isMultiOp ? [] : "";
      }
    } 
    else {
      filter[key] = value?.value ?? value;
    }

    updateState(updated);
  };

  const handleFilterChange = (groupIndex, filterIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    filter.value = { ...filter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  /* ---------------- EXCLUDE CONTACT SUB-FILTER HANDLERS ---------------- */
  const addSubFilter = (groupIndex, filterIndex) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    if (!filter.value) filter.value = {};
    if (!filter.value.subFilters) filter.value.subFilters = [];
    filter.value.subFilters.push({ field: "", operator: "", value: "" });
    updateState(updated);
  };

  const updateSubFilter = (groupIndex, filterIndex, subIndex, key, value) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    const subFilter = filter.value.subFilters[subIndex];

    if (key === "field") {
      const newField = value?.value ?? value;
      subFilter.field = newField;
      subFilter.operator = "";

      const isDateField = EXCLUDE_SUB_DATE_FIELDS.includes(newField);
      if (isDateField) {
        const prefix = EXCLUDE_SUB_PREFIX_MAP[newField] || "";
        subFilter.value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        subFilter.value = "";
      }

      // Fetch dropdown options if needed
      if (DROPDOWN_FIELDS[newField] && !dropdownOptions[newField]) {
        fetchDropdownOptions(newField).then((opts) =>
          setDropdownOptions((prev) => ({ ...prev, [newField]: opts }))
        );
      }
    } else if (key === "operator") {
      const newOp = value?.value ?? value;
      subFilter.operator = newOp;

      const isMultiOp = ["Is Any", "Is All"].includes(newOp);
      const isDateField = EXCLUDE_SUB_DATE_FIELDS.includes(subFilter.field);

      if (isDateField) {
        const prefix = EXCLUDE_SUB_PREFIX_MAP[subFilter.field] || "";
        subFilter.value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        subFilter.value = isMultiOp ? [] : "";
      }
    } else {
      subFilter[key] = value?.value ?? value;
    }

    updateState(updated);
  };

  const removeSubFilter = (groupIndex, filterIndex, subIndex) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    if (filter.value?.subFilters) {
      filter.value.subFilters.splice(subIndex, 1);
      if (filter.value.subFilters.length === 0) {
        filter.value.subFilters = [{ field: "", operator: "", value: "" }];
      }
    }
    updateState(updated);
  };

  const handleSubFilterDateChange = (groupIndex, filterIndex, subIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    const subFilter = filter.value.subFilters[subIndex];
    subFilter.value = { ...subFilter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  const removeRow = (groupIndex, filterIndex) => {
    const updated = [...groups];
    if (updated[groupIndex].filters.length === 1) {
      if (updated.length > 1) {
        updated.splice(groupIndex, 1);
      } else {
        updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
      }
    } else {
      updated[groupIndex].filters.splice(filterIndex, 1);
    }
    updateState(updated);
  };

  /* ---------------- RENDER SUB-FILTERS FOR EXCLUDE CONTACT ---------------- */
  const renderSubFilters = (groupIndex, filterIndex, filter) => {
    if (filter.field !== EXCLUDE_CONTACT_FIELD || filter.operator !== "Matching") {
      return null;
    }

    const subFilters = filter.value?.subFilters || [{ field: "", operator: "", value: "" }];

    return (
      <div className="mt-3 ml-4 pl-4 border-l-2 border-gray-300 space-y-3">
        {subFilters.map((subFilter, subIndex) => (
          <div key={subIndex} className="space-y-2">
            <div className="flex items-center gap-2">
              {subIndex > 0 && (
                <span className="text-sm text-gray-500 w-12">AND</span>
              )}
              <Select
                className="flex-1 dark-H body-N"
                placeholder="Select Field"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
                isSearchable
                options={Object.keys(EXCLUDE_CONTACT_SUB_FIELDS).map((f) => ({ label: f, value: f }))}
                value={subFilter.field ? { label: subFilter.field, value: subFilter.field } : null}
                onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "field", e)}
              />
              <Select
                className="flex-1 dark-H body-N"
                placeholder="Condition"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectPortalStyles}
                isSearchable
                isDisabled={!subFilter.field}
                options={
                  subFilter.field
                    ? EXCLUDE_CONTACT_SUB_FIELDS[subFilter.field].map((c) => ({ label: c, value: c }))
                    : []
                }
                value={subFilter.operator ? { label: subFilter.operator, value: subFilter.operator } : null}
                onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "operator", e)}
              />
              <button
                className="text-red-500 hover:text-red-700 px-2"
                onClick={() => removeSubFilter(groupIndex, filterIndex, subIndex)}
                title="Remove sub-filter"
              >
                ✕
              </button>
            </div>

            {/* Sub-filter value input */}
            {subFilter.operator &&
              !["Is Empty", "Is Not Empty"].includes(subFilter.operator) &&
              !EXCLUDE_SUB_DATE_FIELDS.includes(subFilter.field) && (
                <div className="flex items-center gap-2 ml-12">
                  {DROPDOWN_FIELDS[subFilter.field] ? (
                    <Select
                      className="flex-1 dark-H body-N"
                      placeholder="Select Value"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                      isSearchable
                      isMulti={["Is Any", "Is All"].includes(subFilter.operator)}
                      options={
                        subFilter.field === "State"
                          ? stateOptions
                          : subFilter.field === "Interaction Type"
                          ? activityOptions
                          : dropdownOptions[subFilter.field] || []
                      }
                      value={
                        ["Is Any", "Is All"].includes(subFilter.operator)
                          ? (dropdownOptions[subFilter.field] || []).filter((opt) =>
                              (subFilter.value || []).includes(opt.value)
                            )
                          : (dropdownOptions[subFilter.field] || []).find((opt) => opt.value === subFilter.value)
                      }
                      onChange={(selected) => {
                        const newValue = ["Is Any", "Is All"].includes(subFilter.operator)
                          ? selected?.map((opt) => opt.value) || []
                          : selected?.value || "";
                        updateSubFilter(groupIndex, filterIndex, subIndex, "value", newValue);
                      }}
                    />
                  ) : (
                    <input
                      className="flex-1 border px-2 py-1 rounded dark-H body-N"
                      placeholder="Enter Value"
                      value={subFilter.value || ""}
                      onChange={(e) => updateSubFilter(groupIndex, filterIndex, subIndex, "value", e.target.value)}
                    />
                  )}
                </div>
              )}

            {/* Date sub-filter rendering */}
            {subFilter.operator &&
              !["Is Empty", "Is Not Empty"].includes(subFilter.operator) &&
              EXCLUDE_SUB_DATE_FIELDS.includes(subFilter.field) && (
                <div className="mt-2 ml-12">
                  {(() => {
                    const prefix = EXCLUDE_SUB_PREFIX_MAP[subFilter.field];
                    const operator = subFilter.operator;
                    if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                      return (
                        <input
                          type="number"
                          className="dark-H body-N w-full"
                          placeholder="Enter number of days"
                          min={0}
                          value={subFilter.value?.[`${prefix}count`] || ""}
                          onChange={(e) =>
                            handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}count`, e.target.value)
                          }
                          onWheel={(e) => e.target.blur()}
                        />
                      );
                    } else if (["On", "Before", "After"].includes(operator)) {
                      return (
                        <DatePicker
                          className="dark-H body-N w-full"
                          dateFormat="MM/dd/yyyy"
                          placeholderText="MM/DD/YYYY"
                          maxDate={new Date()}
                          selected={
                            subFilter.value?.[`${prefix}count`]
                              ? moment.tz(subFilter.value[`${prefix}count`], "America/New_York").toDate()
                              : null
                          }
                          onChange={(date) => {
                            const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                            handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}count`, val);
                          }}
                        />
                      );
                    } else if (operator === "Between") {
                      return (
                        <div className="flex flex-row gap-2">
                          <DatePicker
                            className="dark-H body-N"
                            dateFormat="MM/dd/yyyy"
                            placeholderText="Start Date (MM/DD/YYYY)"
                            maxDate={new Date()}
                            selected={
                              subFilter.value?.[`${prefix}start_count`]
                                ? moment.tz(subFilter.value[`${prefix}start_count`], "America/New_York").toDate()
                                : null
                            }
                            onChange={(date) => {
                              const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                              handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}start_count`, val);
                            }}
                          />
                          <DatePicker
                            className="dark-H body-N"
                            dateFormat="MM/dd/yyyy"
                            placeholderText="(MM/DD/YYYY)"
                            maxDate={new Date()}
                            selected={
                              subFilter.value?.[`${prefix}end_count`]
                                ? moment.tz(subFilter.value[`${prefix}end_count`], "America/New_York").toDate()
                                : null
                            }
                            onChange={(date) => {
                              const val = date ? moment.tz(date, "America/New_York").format("YYYY-MM-DD") : "";
                              handleSubFilterDateChange(groupIndex, filterIndex, subIndex, `${prefix}end_count`, val);
                            }}
                          />
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
          </div>
        ))}
        <button className="green-H head-5 text-sm mt-2" onClick={() => addSubFilter(groupIndex, filterIndex)}>
          + Add Criteria
        </button>
      </div>
    );
  };

  /* ---------------- RENDER VALUE INPUT ---------------- */
  const renderValueInput = (groupIndex, filterIndex, filter) => {
    if (!filter.operator || ["Is Empty", "Is Not Empty"].includes(filter.operator)) {
      return null;
    }

    const isDateField = DATE_FIELDS.includes(filter.field);
    const isNumericField = NUMERIC_FIELDS.includes(filter.field);
    const hasDropdown = DROPDOWN_FIELDS[filter.field];
    
    // Exclude Contact with "Matching" operator doesn't use this standard value input
    if (filter.field === EXCLUDE_CONTACT_FIELD && filter.operator === "Matching") {
      return null;
    }

    // Date & Numeric fields (keep existing logic)
    if (isDateField || isNumericField) {
      return null; // Handled separately below
    }

    if (hasDropdown) {
      const isMulti = ["Is Any", "Is All", "Exclude Any"].includes(filter.operator);
      const isStatic = ["state", "activity"].includes(DROPDOWN_FIELDS[filter.field]);
      const Component = isStatic ? Select : AsyncSelect;
      const fieldOptions = dropdownOptions[filter.field] || [];

      const selectedValue = isMulti
        ? fieldOptions.filter((opt) => (filter.value || []).includes(opt.value))
        : fieldOptions.find((opt) => opt.value === filter.value);

      return (
        <Component
          className="flex-1 dark-H body-N"
          placeholder="Select Value"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectPortalStyles}
          isSearchable
          isMulti={isMulti}
          options={isStatic ? fieldOptions : undefined}
          defaultOptions={!isStatic ? fieldOptions : undefined}
          loadOptions={
            !isStatic
              ? async (inputValue) => {
                  const opts = await fetchDropdownOptions(filter.field, inputValue);
                  setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }));
                  return opts;
                }
              : undefined
          }
          value={selectedValue}
          onChange={(selected) => {
            const newValue = isMulti
              ? selected?.map((opt) => opt.value) || []
              : selected?.value || null;
            updateRow(groupIndex, filterIndex, "value", newValue);
          }}
        />
      );
    }

    // Default text input
    return (
      <input
        className="flex-1 border px-2 py-1 rounded dark-H body-N"
        placeholder="Enter Value"
        value={filter.value || ""}
        onChange={(e) => updateRow(groupIndex, filterIndex, "value", e.target.value)}
      />
    );
  };

  /* ---------------- MAIN RENDER ---------------- */
  return (
    <div className="space-y-6 text-black">
      {groups.map((group, groupIndex) => (
        <div key={group.id} className="bg-white space-y-2">
          {groupIndex > 0 && (
            <div className="text-sm font-medium mb-2 text-gray-600">{group.condition} Group</div>
          )}

          {group.filters.map((filter, index) => (
            <div key={index}>
              <div className="flex items-center gap-2">
                {index > 0 && (
                  <Select
                    className="w-24 dark-H body-N"
                    placeholder="Join"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    options={JOIN_OPTIONS}
                    value={filter.join ? { label: filter.join, value: filter.join } : null}
                    onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                  />
                )}

                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Select Field"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  isSearchable
                  options={Object.keys(FIELD_OPTIONS).map((f) => ({ label: f, value: f }))}
                  value={filter.field ? { label: filter.field, value: filter.field } : null}
                  onChange={(e) => updateRow(groupIndex, index, "field", e)}
                />

                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Condition"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  isSearchable
                  isDisabled={!filter.field}
                  options={
                    filter.field
                      ? FIELD_OPTIONS[filter.field].map((c) => ({ label: c, value: c }))
                      : []
                  }
                  value={filter.operator ? { label: filter.operator, value: filter.operator } : null}
                  onChange={(e) => updateRow(groupIndex, index, "operator", e)}
                />

                {renderValueInput(groupIndex, index, filter)}

                <button
                  className="text-red-500 hover:text-red-700 px-2"
                  onClick={() => removeRow(groupIndex, index)}
                  title="Remove filter"
                >
                  ✕
                </button>
              </div>

              {/* Existing Date & Numeric rendering (unchanged) */}
              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                DATE_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    {/* ... your existing date picker logic ... */}
                    {(() => {
                      const prefix = PREFIX_MAP[filter.field];
                      const operator = filter.operator;
                      if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                        return (
                          <input
                            type="number"
                            className="dark-H body-N w-full"
                            placeholder="Enter number of days"
                            min={0}
                            value={filter.value?.[`${prefix}count`] || ""}
                            onChange={(e) => handleFilterChange(groupIndex, index, `${prefix}count`, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        );
                      } else if (["On", "Before", "After"].includes(operator)) {
                        return (
                          <DatePicker
                            className="dark-H body-N w-full"
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                            maxDate={new Date()}
                            selected={filter.value?.[`${prefix}count`] ? moment.tz(filter.value?.[`${prefix}count`], 'America/New_York').toDate() : null}
                            onChange={(date) => {
                              const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                              handleFilterChange(groupIndex, index, `${prefix}count`, val);
                            }}
                          />
                        );
                      } else if (operator === "Between") {
                        return (
                          <div className="flex flex-row gap-2">
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="Start Date (MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}start_count`] ? moment.tz(filter.value?.[`${prefix}start_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}start_count`, val);
                              }}
                            />
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="(MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}end_count`] ? moment.tz(filter.value?.[`${prefix}end_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}end_count`, val);
                              }}
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                NUMERIC_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    <OptionalOption
                      activeTab="date"
                      type="number"
                      title="Properties"
                      onSetLastDate={(value) => handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}count`, value)}
                      date={filter.value?.[`${PREFIX_MAP[filter.field]}count`] || ""}
                      onSetDateRange1={(value) =>
                        handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}start_count`, value)
                      }
                      date2={filter.value?.[`${PREFIX_MAP[filter.field]}start_count`] || ""}
                      onSetDateRange2={(value) =>
                        handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}end_count`, value)
                      }
                      date3={filter.value?.[`${PREFIX_MAP[filter.field]}end_count`] || ""}
                      onClear={() => {
                        handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}count`, "");
                        handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}start_count`, "");
                        handleFilterChange(groupIndex, index, `${PREFIX_MAP[filter.field]}end_count`, "");
                      }}
                    />
                  </div>
                )}

              {/* Render sub-filters for Exclude Contact with Matching operator */}
              {renderSubFilters(groupIndex, index, filter)}
            </div>
          ))}

          <button className="green-H head-5 mt-2" onClick={() => addFilterRow(groupIndex)}>
            + Add Filter
          </button>
        </div>
      ))}
    </div>
  );
}

export default ContactAdvanceFilterData;